import { Avatar, AvatarGroup, Box, ListItemButton, Tooltip, Typography } from "@mui/material"
import type { FC } from "react"

import type { SalesCustomer } from "../../types"
import { ManagerAvatar, isValidManagerName } from "../ManagerAvatar"
import { managerAvatarGroupSx, resultItemSelectedSx } from "../styles"

import type { Location } from "@/graphql/codegen/graphql"
import { AddressFormat } from "@/types/common"
import { formatLocationAddress } from "@/utils/utils"

interface CustomerResultItemProps {
  customer: SalesCustomer
  selected: boolean
  onSelect: (customer: SalesCustomer) => void
}

export const CustomerResultItem: FC<CustomerResultItemProps> = ({ customer, selected, onSelect }) => {
  const address = formatLocationAddress(customer.billingLocation as Location, AddressFormat.AddressOnly)

  const validManagers = customer.productManagers.filter(
    (manager) => isValidManagerName(manager.firstName) || isValidManagerName(manager.lastName)
  )

  return (
    <ListItemButton
      selected={selected}
      onClick={() => onSelect(customer)}
      className='flex min-h-16 items-center justify-between px-4 py-3'
      sx={resultItemSelectedSx}
      data-testid={`sales.new-request-dialog.customer.result-${customer.companyLocationAssociationId}`}
    >
      <Box>
        <Typography className='text-base font-normal leading-6 tracking-[0.15px] text-gray-700'>
          {customer.company.name}
        </Typography>
        {address && <Typography className='text-sm text-gray-500'>{address}</Typography>}
      </Box>
      <AvatarGroup
        max={6}
        sx={managerAvatarGroupSx}
        renderSurplus={(surplus) => (
          <Tooltip
            title={validManagers.map((manager) => `${manager.firstName} ${manager.lastName}`.trim()).join(", ")}
            placement='top'
          >
            <Avatar>+{surplus}</Avatar>
          </Tooltip>
        )}
      >
        {validManagers.map((manager) => (
          <ManagerAvatar key={manager.userId} manager={manager} />
        ))}
      </AvatarGroup>
    </ListItemButton>
  )
}
